import { ActionIcon, Grid, Image } from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { Show } from '../../../../../../../react-commons/components/control-flow/Show';
import type { FormNames } from '../../../../../../../react-commons/utils/form/getFormNames';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    ImageAttachmentPropertyValidation,
    type ImageAttachmentResource,
} from '../../../../../resource/resources/artifacts/attachment/imageAttachmentResource';
import { PropertyInputs } from '../../../../reusable-components/base/property-bases/PropertyInputs';
import type { PropertyValidationInformation } from '../../../../util/PropertyValidationInformation';
import { showNotification } from '../../../../util/showNotification';

export const ImageAttachmentContent = ({
    id,
    form,
    formNames,
    readOnly,
    displayedInOverview,
}: {
    id?: string;
    form: UseFormReturn<ImageAttachmentResource>;
    formNames: FormNames<ImageAttachmentResource>;
    readOnly: boolean;
    displayedInOverview?: boolean;
}) => {
    const getPropertyValidationInformation = (
        propertyIdentifier: ImageAttachmentPropertyValidation
    ): PropertyValidationInformation => ({
        resourceId: id,
        resourceType: ResourceType.IMAGE_ATTACHMENT,
        propertyIdentifier,
    });

    const [fileContentWatcher, fileNameWatcher] = useWatch({
        control: form.control,
        name: [formNames.fileContent, formNames.fileName],
    });

    function _arrayBufferToBase64(buffer: ArrayBuffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    const getExtension = () => {
        const split = fileNameWatcher.split('.');
        const splitLength = split.length;
        return split[splitLength - 1];
    };

    return (
        <Grid>
            <Show when={!displayedInOverview}>
                <Grid.Col span={12}>
                    <PropertyInputs.Name
                        name={formNames.name}
                        control={form.control}
                        readOnly={readOnly}
                        propertyValidationInformation={getPropertyValidationInformation(
                            ImageAttachmentPropertyValidation.NAME
                        )}
                    />
                </Grid.Col>
            </Show>
            <Grid.Col span={12}>
                <PropertyInputs.MultilineString
                    name={formNames.description}
                    control={form.control}
                    nullable={false}
                    readOnly={readOnly}
                    propertyValidationInformation={getPropertyValidationInformation(
                        ImageAttachmentPropertyValidation.DESCRIPTION
                    )}
                />
            </Grid.Col>
            <Grid.Col span={11}>
                <PropertyInputs.String
                    name={formNames.fileName}
                    control={form.control}
                    nullable={false}
                    readOnly={true}
                    propertyValidationInformation={getPropertyValidationInformation(
                        ImageAttachmentPropertyValidation.FILE_NAME
                    )}
                />
            </Grid.Col>
            <Grid.Col span={1}>
                <ActionIcon
                    component="label"
                    variant={'transparent'}
                    size={'small'}
                    disabled={readOnly}
                    color={'var(--mantine-color-base-button)'}
                    pt={30}
                >
                    <IconUpload />
                    <input
                        hidden
                        accept={'image/*'}
                        type={'file'}
                        onChange={(e) => {
                            const files = e.target.files;
                            const fileNames: Array<string> = [];
                            if (files && files.length > 0) {
                                Array.from(files).forEach((file) => {
                                    fileNames.push(file.name);
                                });
                                if ((files.item(0)?.size ?? 0) > 1600000) {
                                    showNotification({ title: 'Image File size is too large!', severity: 'error' });
                                } else {
                                    void files
                                        .item(0)
                                        ?.arrayBuffer()
                                        .then((buffer) => {
                                            form.setValue(formNames.fileContent, _arrayBufferToBase64(buffer), {
                                                shouldDirty: true,
                                            });
                                            form.setValue(formNames.fileName, fileNames[0], { shouldDirty: true });
                                        });
                                }
                            }
                        }}
                    />
                </ActionIcon>
            </Grid.Col>
            <Show when={fileNameWatcher && fileContentWatcher}>
                <Grid.Col span={12}>
                    <Image src={`data:image/${getExtension()};base64,${fileContentWatcher}`} />
                </Grid.Col>
            </Show>
        </Grid>
    );
};
